import { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { Button } from '../../../shared/components/Button';
import { EmptyState } from '../../../shared/components/EmptyState';
import { PageContainer } from '../../../shared/components/PageContainer';
import { INSTALLMENT_STATUS_LABELS } from '../components/format';
import { useCalendarioFerias } from '../hooks/ferias';
import { useEstruturas } from '../hooks/vidaFuncional';

const TONS: Record<string, string> = {
  pendente: 'bg-amber-300',
  programada: 'bg-blue-400',
  em_gozo: 'bg-green-500',
  concluida: 'bg-gray-300',
};

function mesAtual(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo' }).format(new Date()).slice(0, 7);
}

function deslocar(mes: string, n: number): string {
  const [y, m] = mes.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + n, 1));
  return d.toISOString().slice(0, 7);
}

function diasDoMes(mes: string): string[] {
  const [y, m] = mes.split('-').map(Number);
  const total = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return Array.from({ length: total }, (_, i) => `${mes}-${String(i + 1).padStart(2, '0')}`);
}

/** Calendário de férias do RH (US-GP-011A): pendentes, programadas e em gozo, com feriados. */
export default function CalendarioFeriasPage() {
  const [mes, setMes] = useState(mesAtual());
  const [orgUnitId, setOrgUnitId] = useState('');
  const dias = useMemo(() => diasDoMes(mes), [mes]);
  const calendario = useCalendarioFerias(dias[0], dias[dias.length - 1], orgUnitId || undefined);
  const areas = useEstruturas('areas');

  const pessoas = useMemo(() => {
    const map = new Map<string, { name: string; orgUnit: string | null; items: NonNullable<typeof calendario.data>['items'] }>();
    for (const i of calendario.data?.items ?? []) {
      const p = map.get(i.userId) ?? { name: i.name, orgUnit: i.orgUnit, items: [] };
      p.items.push(i);
      map.set(i.userId, p);
    }
    return [...map.entries()].sort((a, b) => a[1].name.localeCompare(b[1].name, 'pt-BR'));
  }, [calendario.data]);
  const feriados = new Map((calendario.data?.holidays ?? []).map((h) => [h.date, h.name]));
  const titulo = new Date(`${mes}-01T12:00:00Z`).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric', timeZone: 'UTC' });

  return (
    <PageContainer title="Calendário de férias" breadcrumbs={[{ label: 'Pessoas' }, { label: 'Calendário de férias' }]}>
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="flex items-center gap-2">
          <Button size="sm" variant="secondary" onClick={() => setMes(deslocar(mes, -1))} aria-label="Mês anterior"><ChevronLeft className="h-4 w-4" /></Button>
          <span className="min-w-40 text-center font-medium capitalize text-gray-900">{titulo}</span>
          <Button size="sm" variant="secondary" onClick={() => setMes(deslocar(mes, 1))} aria-label="Próximo mês"><ChevronRight className="h-4 w-4" /></Button>
        </div>
        <div className="w-64">
          <AutocompleteSelect label="Área" options={[{ value: '', label: 'Todas' }, ...(areas.data ?? []).map((a) => ({ value: a.id, label: a.name }))]}
            value={orgUnitId} onChange={setOrgUnitId} />
        </div>
        <div className="flex flex-wrap gap-3 text-xs text-gray-600">
          {Object.entries(TONS).map(([k, c]) => (
            <span key={k} className="flex items-center gap-1"><span className={`inline-block h-3 w-3 rounded ${c}`} />{INSTALLMENT_STATUS_LABELS[k]}</span>
          ))}
          <span className="flex items-center gap-1"><span className="inline-block h-3 w-3 rounded bg-red-100" />Feriado</span>
        </div>
      </div>

      {calendario.isLoading ? (
        <p className="text-sm text-gray-500">Carregando…</p>
      ) : pessoas.length === 0 ? (
        <EmptyState title="Nenhuma férias no mês" />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
          <table className="text-xs">
            <thead>
              <tr>
                <th className="sticky left-0 min-w-48 bg-white px-2 py-1 text-left">Colaborador</th>
                {dias.map((d) => {
                  const dow = new Date(`${d}T00:00:00Z`).getUTCDay();
                  return (
                    <th key={d} title={feriados.get(d)}
                      className={`w-6 px-0.5 py-1 text-center font-normal ${feriados.has(d) ? 'bg-red-100' : dow === 0 || dow === 6 ? 'bg-gray-50' : ''}`}>
                      {Number(d.slice(8))}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {pessoas.map(([userId, p]) => (
                <tr key={userId} className="border-t border-gray-100">
                  <td className="sticky left-0 bg-white px-2 py-1">
                    <span className="block text-gray-900">{p.name}</span>
                    {p.orgUnit && <span className="text-gray-500">{p.orgUnit}</span>}
                  </td>
                  {dias.map((d) => {
                    const hit = p.items.find((i) => i.startDate <= d && i.endDate >= d);
                    return (
                      <td key={d} className={`h-6 ${feriados.has(d) ? 'bg-red-50' : ''}`}
                        title={hit ? `${INSTALLMENT_STATUS_LABELS[hit.status]}: ${hit.startDate} a ${hit.endDate}` : undefined}>
                        {hit && <span className={`block h-4 ${TONS[hit.status] ?? 'bg-gray-300'}`} />}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </PageContainer>
  );
}
